export const API = process.env.REACT_APP_API_URL || "http://localhost:3001";
export const GROQ_KEY = process.env.REACT_APP_GROQ_KEY || "";
export const HUNTER_KEY = process.env.REACT_APP_HUNTER_KEY || "";
export const PIPELINE_PAGE_SIZE = 15;

export const EMPTY_LEAD_META = {
  total: 0,
  hot: 0,
  good: 0,
  noSite: 0,
  withEmail: 0,
  contacted: 0,
  interested: 0,
  won: 0,
  lost: 0,
  doIt: 0,
  referOut: 0,
  avgScore: 0,
  uniqueSources: [],
  topCountries: {},
  sourceCounts: {},
};

export function scoreLabel(score) {
  if (score >= 80) return { label: "Hot", emoji: "fire", color: "#ef4444" };
  if (score >= 60) return { label: "Good", emoji: "bolt", color: "#f59e0b" };
  if (score >= 40) return { label: "Potential", emoji: "seedling", color: "#10b981" };
  return { label: "Cold", emoji: "snowflake", color: "#94a3b8" };
}

export function worthAssessment(lead) {
  const score = Number(lead?.score || 0);
  const hasEmail = !!(lead?.email || lead?.projectContactEmails?.[0]);
  const hasPhone = !!(lead?.phone || lead?.projectContactPhones?.[0]);
  const hasLinkedin = !!lead?.linkedin;
  const hasDirectContact = hasEmail || hasPhone;

  if (lead?.decision === "Do it" || lead?.aiRecommendation === "do_it") {
    return { key: "yes", label: "Worth", reason: "AI or decision recommends pursuing this lead." };
  }
  if (hasDirectContact && score >= 45) {
    return { key: "yes", label: "Worth", reason: "Has direct contact path and a healthy lead score." };
  }
  if (hasDirectContact || hasLinkedin || score >= 35) {
    return { key: "review", label: "Review", reason: "Partially qualified. Needs quick manual check before outreach." };
  }
  return { key: "no", label: "Low", reason: "No direct contact path and low qualification score." };
}

function isAggregatorWebsite(url) {
  if (!url) return false;
  try {
    const host = new URL(String(url).startsWith("http") ? String(url) : `https://${url}`)
      .hostname.replace(/^www\./, "").toLowerCase();
    const blocked = [
      "google.com", "news.google.com", "bing.com", "yahoo.com", "duckduckgo.com",
      "linkedin.com", "facebook.com", "instagram.com", "x.com", "twitter.com",
      "youtube.com", "tiktok.com", "reddit.com", "pinterest.com",
      "businesswire.com", "prnewswire.com", "globenewswire.com", "accesswire.com",
      "einpresswire.com", "newswire.com", "marketwatch.com", "reuters.com",
      "apnews.com", "bloomberg.com", "cnbc.com", "bbc.com", "forbes.com",
      "fortune.com", "techcrunch.com", "theverge.com", "zdnet.com", "cnet.com",
      "venturebeat.com", "inc.com", "entrepreneur.com", "businessinsider.com",
      "wsj.com", "nytimes.com", "washingtonpost.com", "ft.com", "economist.com",
      "crunchbase.com", "dnb.com", "zoominfo.com", "owler.com", "manta.com",
      "yellowpages.com", "yelp.com", "trustpilot.com", "glassdoor.com", "indeed.com",
      "clutch.co", "g2.com", "capterra.com", "angellist.com", "wellfound.com",
      "producthunt.com", "medium.com", "substack.com", "wordpress.com", "blogspot.com",
      "tumblr.com", "quora.com", "wikipedia.org", "wikimedia.org", "github.com",
      "gitlab.com", "stackoverflow.com", "npmjs.com", "microsoft.com", "azure.com",
      "amazonaws.com", "apple.com",
    ];
    return blocked.some((domain) => host === domain || host.endsWith(`.${domain}`));
  } catch {
    return false;
  }
}

export function hasUsableWebsite(lead) {
  return !!(lead?.website && !isAggregatorWebsite(lead.website));
}

export function inferWebsiteFromOpportunityItem(item) {
  const normalizeWebsite = (rawUrl) => {
    if (!rawUrl) return null;
    try {
      const url = new URL(String(rawUrl).startsWith("http") ? String(rawUrl) : `https://${rawUrl}`);
      const host = url.hostname.replace(/^www\./, "").toLowerCase();
      if (!host || isAggregatorWebsite(host)) return null;
      return `https://${host}`;
    } catch {
      return null;
    }
  };

  const sourceLink = item?.link || "";
  try {
    const parsed = new URL(sourceLink);
    const realUrl = parsed.searchParams.get("url") || parsed.searchParams.get("u") || "";
    const fromReal = normalizeWebsite(realUrl);
    if (fromReal) return fromReal;
    const fromSource = normalizeWebsite(parsed.href);
    if (fromSource) return fromSource;
  } catch {
    // Fall through to text detection.
  }

  const text = `${item?.title || ""} ${item?.description || ""}`;
  const domainMatch = text.match(/\b([a-z0-9][a-z0-9.-]*\.[a-z]{2,})(?:\/[\w./?%&=-]*)?\b/i);
  return normalizeWebsite(domainMatch?.[1]) || null;
}

export function inferCountryFromOpportunityItem(item, fallback = "Global") {
  const text = `${item?.title || ""} ${item?.description || ""}`.toLowerCase();

  const keywordPatterns = [
    { country: "United Kingdom", patterns: [/\bunited kingdom\b/, /\buk\b/, /\bbritain\b/, /\bengland\b/] },
    { country: "USA", patterns: [/\busa\b/, /\bunited states\b/, /\bu\.s\.a\b/, /\bu\.s\.\b/, /\bus market\b/] },
    { country: "UAE", patterns: [/\buae\b/, /\bunited arab emirates\b/, /\bemirates\b/] },
    { country: "Saudi Arabia", patterns: [/\bsaudi arabia\b/, /\bksa\b/] },
    { country: "South Africa", patterns: [/\bsouth africa\b/] },
    { country: "Netherlands", patterns: [/\bnetherlands\b/, /\bdutch\b/] },
    { country: "Singapore", patterns: [/\bsingapore\b/] },
    { country: "Australia", patterns: [/\baustralia\b/] },
    { country: "Canada", patterns: [/\bcanada\b/] },
    { country: "Germany", patterns: [/\bgermany\b/, /\bgerman\b/] },
    { country: "Sweden", patterns: [/\bsweden\b/, /\bswedish\b/] },
    { country: "Denmark", patterns: [/\bdenmark\b/, /\bdanish\b/] },
    { country: "Norway", patterns: [/\bnorway\b/, /\bnorwegian\b/] },
    { country: "India", patterns: [/\bindia\b/, /\bindian\b/] },
  ];

  for (const entry of keywordPatterns) {
    if (entry.patterns.some((pattern) => pattern.test(text))) return entry.country;
  }

  const tldToCountry = {
    in: "India",
    uk: "United Kingdom",
    gb: "United Kingdom",
    ae: "UAE",
    us: "USA",
    sg: "Singapore",
    au: "Australia",
    ca: "Canada",
    de: "Germany",
    nl: "Netherlands",
    se: "Sweden",
    dk: "Denmark",
    no: "Norway",
    sa: "Saudi Arabia",
    za: "South Africa",
  };

  const tryUrl = (rawUrl) => {
    if (!rawUrl) return null;
    try {
      const url = new URL(rawUrl);
      const host = url.hostname.replace(/^www\./, "").toLowerCase();
      const parts = host.split(".");
      return tldToCountry[parts[parts.length - 1]] || null;
    } catch {
      return null;
    }
  };

  return tryUrl(inferWebsiteFromOpportunityItem(item)) || tryUrl(item?.link || "") || fallback;
}

export function calcScore(lead) {
  // ── Tender-specific scoring (SAM, Doffin, TED, Contracts Finder, etc.) ──
  const src = String(lead?.source || "").toLowerCase();
  const isTender =
    src.includes("doffin") || src.includes("sam") || src.includes("ted") ||
    src.includes("tender") || src.includes("contracts") || src.includes("procurement");

  if (isTender) {
    let score = 45; // All active tenders are worth considering
    if (lead?.email) score += 15;           // Has a point-of-contact email
    if (lead?.tenderBuyer) score += 5;      // Buyer/agency is identifiable
    if (lead?.tenderDeadline) {
      try {
        const daysLeft = (new Date(lead.tenderDeadline) - Date.now()) / 86400000;
        if (daysLeft > 0 && daysLeft <= 14) score += 22; // Urgent – closing soon
        else if (daysLeft > 0 && daysLeft <= 30) score += 13;
        else if (daysLeft > 0 && daysLeft <= 60) score += 6;
        // past deadline: no urgency bonus, tender may still be viewable
      } catch (_) { /* ignore bad dates */ }
    }
    return Math.min(score, 100);
  }

  // ── Standard B2B company scoring ──
  let score = 0;
  const employees = Number(lead?.employees || 0);
  const usableWebsite = hasUsableWebsite(lead);
  const websiteStatus = usableWebsite ? (lead?.websiteStatus || "found") : (lead?.websiteStatus || "unknown");

  if (websiteStatus === "missing") score += 35;
  else score += 5;

  if (employees >= 1 && employees <= 5) score += 30;
  else if (employees >= 6 && employees <= 20) score += 25;
  else if (employees >= 21 && employees <= 50) score += 15;
  else if (employees >= 51 && employees <= 200) score += 10;
  else score += 20;

  const industry = String(lead?.industry || "").toLowerCase();
  const highFit = ["retail", "restaurant", "food", "construction", "design", "marketing", "fashion", "beauty", "gym", "fitness", "salon", "real estate", "hotel", "hospitality", "event", "craft", "trade", "plumbing", "electrical", "tannlege", "frisor", "rorlegger", "maler", "snekker", "bakeri", "blomster", "kafe"];
  const goodFit = ["it", "software", "consulting", "education", "health", "legal", "finance", "accounting", "recruitment", "logistics", "transport", "media", "sport"];
  if (highFit.some((keyword) => industry.includes(keyword))) score += 25;
  else if (goodFit.some((keyword) => industry.includes(keyword))) score += 10;
  else score += 15;

  if (lead?.phone) score += 5;
  if (lead?.email) score += 5;

  const finalScore = Math.min(score, 100);
  let priority = finalScore;
  if (websiteStatus === "missing") priority += 20;
  else if (websiteStatus === "unknown") priority += 10;
  return priority;
}

export function localQualify(lead) {
  const score = calcScore(lead);
  const reasons = [];

  if (lead?.websiteStatus === "missing") reasons.push("No website found - strong digital opportunity");
  if (lead?.employees && lead.employees <= 20) reasons.push("Small team - faster buying decisions");
  if (lead?.industry) reasons.push(`Industry fit: ${lead.industry}`);
  if (lead?.city || lead?.country) reasons.push(`Local context: ${[lead.city, lead.country].filter(Boolean).join(", ")}`);

  const recommendation = score >= 70 ? "do_it" : score >= 45 ? "refer_out" : "skip";
  const estimatedValue = score >= 75 ? "large" : score >= 55 ? "medium" : "small";

  return {
    score,
    reasons: reasons.slice(0, 4),
    projectType: lead?.websiteStatus === "missing" ? "web" : "other",
    estimatedValue,
    recommendation,
    summary: lead?.websiteStatus === "missing"
      ? "High opportunity: no website and clear room for digital improvement."
      : "Potential lead - focus on quick value proposition and next step.",
  };
}

export function localOutreach(lead, channel, tone = "Friendly") {
  const usableWebsite = hasUsableWebsite(lead);
  const serviceFocus = lead?.websiteStatus === "missing"
    ? "a simple website and lead capture page"
    : "a conversion-focused improvement to your current online presence";
  const location = [lead?.city, lead?.country].filter(Boolean).join(", ");
  const toneLine = tone === "Direct"
    ? "Quick note"
    : tone === "Premium"
      ? "I noticed a clear opportunity and wanted to share a tailored idea"
      : "I thought this might be useful";

  return `Hi ${lead?.name || "there"},\n\n${toneLine}. I work with businesses ${location ? `in ${location} ` : ""}to improve digital lead flow. I noticed ${usableWebsite ? "your online presence" : "you may not have a website yet"}. I can help with ${serviceFocus}, plus UX and outreach setup so you can get more qualified inquiries.\n\nOpen to a quick 15-minute chat this week?\n\nBest,\nMaha`;
}

export async function groqChat(systemPrompt, userPrompt) {
  const response = await fetch("https://api.groq.com/openai/v1/chat/completions", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${GROQ_KEY}`,
    },
    body: JSON.stringify({
      model: "llama-3.3-70b-versatile",
      max_tokens: 800,
      messages: [
        { role: "system", content: systemPrompt },
        { role: "user", content: userPrompt },
      ],
    }),
  });
  const data = await response.json();
  return data.choices?.[0]?.message?.content || "";
}

export const SOURCES = [
  { id: "OPP", label: "Public Opportunities", type: "PUBLIC", color: "#06b6d4" },
  { id: "DOFFIN", label: "Doffin Tenders", type: "TENDER", color: "#ef4444" },
  { id: "SAM", label: "SAM.gov (USA)", type: "TENDER", color: "#ef4444", flag: "us" },
  { id: "CF", label: "Contracts Finder (UK)", type: "TENDER", color: "#ef4444", flag: "gb" },
  { id: "TED", label: "TED Europa (EU)", type: "TENDER", color: "#ef4444" },
  { id: "NO", label: "Norway", type: "LIVE", color: "#3b82f6" },
  { id: "GB", label: "United Kingdom", type: "LIVE", color: "#3b82f6" },
  { id: "IN", label: "India", type: "MAPS", color: "#8b5cf6" },
  { id: "AE", label: "UAE", type: "MAPS", color: "#8b5cf6" },
  { id: "US", label: "USA", type: "MAPS", color: "#8b5cf6" },
  { id: "SG", label: "Singapore", type: "MAPS", color: "#8b5cf6" },
  { id: "AU", label: "Australia", type: "MAPS", color: "#8b5cf6" },
  { id: "CA", label: "Canada", type: "MAPS", color: "#8b5cf6" },
  { id: "DE", label: "Germany", type: "MAPS", color: "#8b5cf6" },
  { id: "NL", label: "Netherlands", type: "MAPS", color: "#8b5cf6" },
  { id: "SE", label: "Sweden", type: "MAPS", color: "#8b5cf6" },
  { id: "DK", label: "Denmark", type: "LIVE", color: "#3b82f6" },
  { id: "SA", label: "Saudi Arabia", type: "MAPS", color: "#8b5cf6" },
  { id: "ZA", label: "South Africa", type: "MAPS", color: "#8b5cf6" },
];

export const INDUSTRIES = [
  "All Industries", "Restaurant / Food", "Retail / Shop", "Construction", "IT / Software",
  "Marketing / Agency", "Design / Creative", "Health / Clinic", "Education", "Consulting",
  "Real Estate", "Legal", "Finance / Accounting", "Logistics", "Manufacturing", "Hotel / Hospitality",
  "Fitness / Gym", "Beauty / Salon", "Event / Entertainment", "Trade / Plumbing / Electrical",
];

export const CITY_MAP = {
  NO: ["All Cities", "Oslo", "Bergen", "Trondheim", "Stavanger", "Tromso", "Kristiansand", "Drammen"],
  GB: ["All Cities", "London", "Manchester", "Birmingham", "Leeds", "Glasgow", "Edinburgh", "Bristol"],
  IN: ["All Cities", "Mumbai", "Delhi", "Bangalore", "Chennai", "Hyderabad", "Pune", "Kolkata"],
  AE: ["All Cities", "Dubai", "Abu Dhabi", "Sharjah"],
  US: ["All Cities", "New York", "Los Angeles", "Chicago", "Houston", "Dallas", "Miami", "Seattle"],
  SG: ["All Cities", "Singapore"],
  AU: ["All Cities", "Sydney", "Melbourne", "Brisbane", "Perth", "Adelaide"],
  CA: ["All Cities", "Toronto", "Vancouver", "Montreal", "Calgary", "Ottawa"],
  DE: ["All Cities", "Berlin", "Munich", "Hamburg", "Frankfurt", "Cologne", "Stuttgart"],
  NL: ["All Cities", "Amsterdam", "Rotterdam", "The Hague", "Utrecht", "Eindhoven"],
  SE: ["All Cities", "Stockholm", "Gothenburg", "Malmo", "Uppsala"],
  DK: ["All Cities", "Copenhagen", "Aarhus", "Odense", "Aalborg"],
  SA: ["All Cities", "Riyadh", "Jeddah", "Dammam"],
  ZA: ["All Cities", "Johannesburg", "Cape Town", "Durban", "Pretoria"],
  OPP: ["All Cities"],
  DOFFIN: ["All Cities"],
  SAM: ["All Cities"],
  CF: ["All Cities"],
  TED: ["All Cities"],
};

export const OPPORTUNITY_COUNTRY_OPTIONS = [
  { id: "GLOBAL", label: "Global" },
  ...SOURCES.filter((source) => ["LIVE", "MAPS"].includes(source.type)).map((source) => ({ id: source.id, label: source.label })),
];

export const SKILLS_MAP = {
  "Web & App": ["Website Design", "React / Vue", "Mobile App", "E-commerce", "Web Animation", "Landing Page"],
  "UX & Design": ["UX Research", "UI Design", "Wireframing", "Branding", "Logo Design", "Figma"],
  "Tenders & Proposals": ["RFP Writing", "Bid Strategy", "Tender Research", "Grant Writing", "Proposal Design"],
  Marketing: ["SEO", "Google Ads", "Social Media", "Email Marketing", "Content Writing", "Video Production"],
  "Data & Tech": ["API Integration", "Automation", "CRM Setup", "Analytics", "AI Tools", "Data Visualisation"],
  Consulting: ["Digital Strategy", "Business Analysis", "Project Management", "Change Management", "Training"],
};